import { useState, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth, type DriverProfile } from '../../lib/auth';
import { useSetPageHeader } from '../../components/app/AppShell';
import { DocPicker } from '../../components/form/DocPicker';
import { apiFetch } from '../../lib/api-client';
import { invalidateDoc } from '../../lib/doc-cache';
import { loadKycDraft, saveKycDraft, clearKycDraft } from '../../lib/kyc-draft';
import { formatAadhaar } from '../../lib/aadhaar';
import { haptic } from '../../lib/haptic';

const errorStyle: CSSProperties = { color: '#B91C1C', fontSize: 12, margin: 0 };

export function KycAadhaarEditRoute() {
  const { t } = useTranslation('profile');
  useSetPageHeader({ backTo: '/profile/kyc', title: t('kyc.aadhaarTitle') }, []);
  const navigate = useNavigate();
  const driver = useAuth(s => s.driver);

  const [aadhaarNumber, setAadhaarNumber] = useState(() => loadKycDraft<{ aadhaarNumber?: string }>(driver?.id, 'aadhaar')?.aadhaarNumber ?? driver?.aadhaarNumber ?? '');
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null>(null);
  const [touched, setTouched] = useState<{ num: boolean; photo: boolean }>({ num: false, photo: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const d = loadKycDraft<{ aadhaarNumber?: string }>(driver?.id, 'aadhaar');
    setAadhaarNumber(d?.aadhaarNumber ?? driver?.aadhaarNumber ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-seed (draft-aware) on driver identity change
  }, [driver?.id]);

  // Persist the typed value as a draft so a background/return doesn't lose it (#500).
  useEffect(() => {
    if (driver?.id && touched.num) saveKycDraft(driver.id, 'aadhaar', { aadhaarNumber });
  }, [aadhaarNumber, touched.num, driver?.id]);

  const aadhaarValid = aadhaarNumber.length === 0 || aadhaarNumber.length === 12;
  const canSave = aadhaarValid && !busy;

  const handleSave = async () => {
    if (!driver) return;
    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, string> = {};
      if (touched.num) payload.aadhaarNumber = aadhaarNumber;
      if (touched.photo && photoDataUrl) payload.aadhaarUrl = photoDataUrl;

      try {
        await apiFetch('/driver-app/me/kyc', {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });
      } catch (e) {
        if ((e as { status?: number })?.status === 401) {
          navigate('/auth', { replace: true });
          return;
        }
        throw e;
      }
      const updated = await apiFetch<DriverProfile>('/driver-app/me');
      useAuth.setState({ driver: updated });
      await invalidateDoc('aadhaar');
      clearKycDraft(driver.id, 'aadhaar');
      haptic.success();
      navigate('/profile/kyc');
    } catch (e: unknown) {
      const errMsg = (e as { message?: string } | null | undefined)?.message;
      setError(errMsg || t('kyc.submitError'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-5 pt-2 pb-10">
      <div className="space-y-3 mb-4">
        <div>
          <label htmlFor="aadhaar-input" className="block text-sm font-semibold mb-1">{t('signupFields.aadhaarNumber', { ns: 'auth' })}</label>
          <input
            id="aadhaar-input"
            type="text"
            autoComplete="off"
            data-1p-ignore="true"
            data-lpignore="true"
            data-bwignore="true"
            inputMode="numeric"
            value={formatAadhaar(aadhaarNumber)}
            onChange={(e) => {
              const raw = e.target.value.replace(/\D/g, '').slice(0, 12);
              setAadhaarNumber(raw);
              setTouched(p => ({ ...p, num: true }));
            }}
            className="w-full px-3 py-3 border border-[var(--color-border)] rounded-[var(--radius-md)] text-base"
          />
          {aadhaarNumber.length > 0 && !aadhaarValid ? <p style={errorStyle}>{t('signupValidation.aadhaarInvalid', { ns: 'auth', defaultValue: 'Aadhaar must be exactly 12 digits' })}</p> : null}
        </div>

        <DocPicker
          value={photoDataUrl}
          onChange={(dataUrl) => { setPhotoDataUrl(dataUrl); setTouched(p => ({ ...p, photo: true })); }}
          label={driver?.hasAadhaar ? t('kyc.photoOnFile') : t('kyc.noPhotoYet')}
          accept="image/*,application/pdf"
        />
      </div>

      {error ? <p role="alert" style={{ ...errorStyle, margin: '0 0 0.5rem' }}>{error}</p> : null}

      <button
        type="button"
        disabled={!canSave}
        onClick={() => { haptic.select(); handleSave(); }}
        className="w-full py-3 rounded-full font-bold disabled:opacity-50"
        style={{ background: 'var(--color-btn-primary)', color: '#fff', minHeight: 'var(--touch-target, 52px)' }}
      >
        {busy ? t('kyc.save') + '…' : t('kyc.save')}
      </button>
    </div>
  );
}
